import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import { useLazyGetHTSDescriptionsQuery } from '@/services';

interface HTSDescription {
  description: string | null;
  isLoading: boolean;
  error?: string;
}

interface HTSDescriptionsContextType {
  descriptions: Record<string, HTSDescription>;
  fetchDescription: (htsCode: string) => Promise<void>;
  clearCache: () => void;
}

const HTSDescriptionsContext = createContext<HTSDescriptionsContextType | undefined>(undefined);

interface HTSDescriptionsProviderProps {
  children: ReactNode;
}

export const HTSDescriptionsProvider: React.FC<HTSDescriptionsProviderProps> = ({ children }) => {
  const [descriptions, setDescriptions] = useState<Record<string, HTSDescription>>({});
  const [getHTSDescriptions] = useLazyGetHTSDescriptionsQuery();

  const fetchDescription = useCallback(async (htsCode: string) => {
    // If already exists (loading, error, or success), don't fetch again
    if (descriptions[htsCode]) {
      return;
    }

    // Set loading state
    setDescriptions(prev => ({
      ...prev,
      [htsCode]: { description: null, isLoading: true }
    }));

    try {
      const response = await getHTSDescriptions({
        htsCodes: [htsCode]
      }).unwrap();

      const description = response.getHTSDescriptions?.[0]?.description || null;
      
      setDescriptions(prev => ({
        ...prev,
        [htsCode]: { description, isLoading: false }
      }));
    } catch (error) {
      console.error('Error fetching HTS description:', error);
      setDescriptions(prev => ({
        ...prev,
        [htsCode]: { 
          description: null, 
          isLoading: false, 
          error: 'Failed to load description' 
        }
      }));
    }
  }, [getHTSDescriptions, descriptions]);

  const clearCache = useCallback(() => {
    setDescriptions({});
  }, []);

  const contextValue: HTSDescriptionsContextType = {
    descriptions,
    fetchDescription,
    clearCache,
  };

  return (
    <HTSDescriptionsContext.Provider value={contextValue}>
      {children}
    </HTSDescriptionsContext.Provider>
  );
};

export const useHTSDescriptions = (): HTSDescriptionsContextType => {
  const context = useContext(HTSDescriptionsContext);
  if (!context) {
    throw new Error('useHTSDescriptions must be used within an HTSDescriptionsProvider');
  }
  return context;
};

export default HTSDescriptionsProvider;